import { useEffect, useState } from "react";

// System logos (Images/Icons/<System>_Icon[_Dark].png) and the dark-theme flag, shared by the
// Launch page tiles (IntegrationRow) and the Match Tools Send/Get buttons so both draw the same
// source -> target logos.

declare const require: {
    context: (
        path: string,
        useSubdirectories?: boolean,
        regExp?: RegExp
    ) => {
        keys: () => string[];
        <T = any>(id: string): T;
    };
};

type SystemIconFiles = {
    default?: string;
    dark?: string;
};

// require.context is a webpack build-time feature; jest (NODE_ENV "test") has none, so the icon
// map is empty there. Webpack inlines NODE_ENV, so the build keeps only the require.context branch.
const iconContext =
    process.env.NODE_ENV === "test"
        ? null
        : require.context(
              "../../Images/Icons",
              false,
              /_Icon(?:_Dark)?\.png$/i
          );

const SYSTEM_ICONS = buildSystemIconsMap();

export function normalizeSystemKey(value: string | null | undefined): string {
    if (!value) return "";
    return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function parseCompanySystems(value: string | undefined): string[] {
    if (!value) return [];

    const seen = new Set<string>();
    const systems: string[] = [];
    for (const system of value.split("|")) {
        const name = system.trim();
        const key = normalizeSystemKey(name);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        systems.push(name);
    }
    return systems;
}

function buildSystemIconsMap(): Record<string, SystemIconFiles> {
    const files: Record<string, SystemIconFiles> = {};
    if (!iconContext) return files;

    for (const filePath of iconContext.keys()) {
        const match = filePath.match(/^\.\/(.+?)_Icon(_Dark)?\.png$/i);
        if (!match) continue;

        const systemKey = normalizeSystemKey(match[1]);
        if (!systemKey) continue;

        const imported = iconContext<{ default?: string } | string>(filePath);
        const resolvedSrc =
            typeof imported === "string" ? imported : imported.default;
        if (!resolvedSrc) continue;

        const next = files[systemKey] ?? {};
        if (match[2]) {
            next.dark = resolvedSrc;
        } else {
            next.default = resolvedSrc;
        }
        files[systemKey] = next;
    }

    return files;
}

// A system whose display name differs from its icon file's name: the MriX match tools label
// their table "MRI", the logo is MRIX_Icon.png.
const SYSTEM_ICON_ALIASES: Record<string, string> = {
    mri: "mrix",
};

export function iconKeyFor(system: string): string {
    return SYSTEM_ICON_ALIASES[system] ?? system;
}

export function resolveSystemIcon(
    system: string | undefined,
    useDarkVariant: boolean
): string | undefined {
    if (!system) return undefined;

    const icon = SYSTEM_ICONS[iconKeyFor(system)];
    if (!icon) return undefined;

    if (useDarkVariant && icon.dark) {
        return icon.dark;
    }

    return icon.default;
}

/** True while the Portal's dark theme is on; follows the data-theme toggle live. */
export function useIsDarkTheme(): boolean {
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    useEffect(() => {
        if (typeof document === "undefined") return;

        const detectDarkTheme = () =>
            Boolean(document.querySelector('[data-theme="dark"]'));

        setIsDarkTheme(detectDarkTheme());

        const observer = new MutationObserver(() => {
            setIsDarkTheme(detectDarkTheme());
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-theme"],
        });

        if (document.body) {
            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ["data-theme"],
            });
        }

        return () => observer.disconnect();
    }, []);

    return isDarkTheme;
}
